/**
 * Real API overview metrics for BaaS console overview tab.
 * Top endpoints exclude MobileService ↔ Baas console/IB proxy catalog routes.
 */

const isConsoleInfrastructurePath = (path) => {
  const normalized = String(path || "").trim().toLowerCase().replace(/\/+$/, "") || "/";
  const withSlash = normalized.startsWith("/") ? normalized : `/${normalized}`;
  if (
    withSlash.includes("/internal-transfer-options")
    || withSlash.includes("/peer-roots")
    || withSlash.includes("/peer-statements")
    || withSlash.includes("/peer-balances")
    || withSlash.includes("/peer-pix-metrics")
    || withSlash.includes("/peer-owners")
  ) {
    return true;
  }
  return /^\/v1\/instances\/\d+(\/(features|scopes))?$/.test(withSlash);
};

const mapTopEndpoint = (row) => ({
  method: String(row?.method ?? "GET"),
  path: String(row?.path ?? ""),
  requests: Number(row?.requests ?? 0),
  avgLatencyMs: Number(row?.avg_latency_ms ?? row?.avgLatencyMs ?? 0),
  errorRate: Number(row?.error_rate ?? row?.errorRate ?? 0),
});

const mapOverviewMetrics = (data) => ({
  healthStatus: data?.health_status ?? data?.healthStatus ?? "healthy",
  totalRequests: Number(data?.total_requests ?? data?.totalRequests ?? 0),
  successRate: Number(data?.success_rate ?? data?.successRate ?? 100),
  avgLatencyMs: Number(data?.avg_latency_ms ?? data?.avgLatencyMs ?? 0),
  errorRate: Number(data?.error_rate ?? data?.errorRate ?? 0),
  activeApiKeys: Number(data?.active_api_keys ?? data?.activeApiKeys ?? 0),
  webhookDeliveryRate: Number(
    data?.webhook_delivery_rate ?? data?.webhookDeliveryRate ?? 100,
  ),
  requestsOverTime: {
    labels: Array.isArray(data?.requests_over_time?.labels)
      ? data.requests_over_time.labels
      : Array.isArray(data?.requestsOverTime?.labels)
        ? data.requestsOverTime.labels
        : [],
    values: Array.isArray(data?.requests_over_time?.values)
      ? data.requests_over_time.values.map(Number)
      : Array.isArray(data?.requestsOverTime?.values)
        ? data.requestsOverTime.values.map(Number)
        : [],
  },
  statusBreakdown: {
    success2xx: Number(
      data?.status_breakdown?.success_2xx ??
        data?.statusBreakdown?.success2xx ??
        0,
    ),
    clientError4xx: Number(
      data?.status_breakdown?.client_error_4xx ??
        data?.statusBreakdown?.clientError4xx ??
        0,
    ),
    serverError5xx: Number(
      data?.status_breakdown?.server_error_5xx ??
        data?.statusBreakdown?.serverError5xx ??
        0,
    ),
  },
  topEndpoints: (Array.isArray(data?.top_endpoints)
      ? data.top_endpoints
      : Array.isArray(data?.topEndpoints)
        ? data.topEndpoints
        : []
    )
      .map(mapTopEndpoint)
      .filter((row) => !isConsoleInfrastructurePath(row.path)),
  healthSummary: {
    status: data?.health_summary?.status ?? data?.healthSummary?.status ?? "healthy",
    incidents: Array.isArray(data?.health_summary?.incidents)
      ? data.health_summary.incidents
      : Array.isArray(data?.healthSummary?.incidents)
        ? data.healthSummary.incidents
        : [],
  },
});

const emptyOverviewMetrics = (activeApiKeys = 0) => ({
  healthStatus: "healthy",
  totalRequests: 0,
  successRate: 100,
  avgLatencyMs: 0,
  errorRate: 0,
  activeApiKeys,
  webhookDeliveryRate: 100,
  requestsOverTime: {
    labels: ["00h", "01h", "02h", "03h", "04h", "05h", "06h", "07h", "08h", "09h", "10h", "11h", "12h", "13h", "14h", "15h", "16h", "17h", "18h", "19h", "20h", "21h", "22h", "23h"],
    values: Array(24).fill(0),
  },
  statusBreakdown: {
    success2xx: 0,
    clientError4xx: 0,
    serverError5xx: 0,
  },
  topEndpoints: [],
  healthSummary: {
    status: "healthy",
    incidents: [],
  },
});

/**
 * @param {object} data — raw API response
 * @param {{ activeApiKeys?: number }} [options]
 */
export const mapBaasApiOverviewMetrics = (data, options = {}) => {
  if (!data) {
    return emptyOverviewMetrics(options.activeApiKeys ?? 0);
  }

  const mapped = mapOverviewMetrics(data);
  if (!mapped.activeApiKeys && options.activeApiKeys) {
    mapped.activeApiKeys = options.activeApiKeys;
  }

  return mapped;
};

export { emptyOverviewMetrics };
